import { Suspense } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { PhoneInput } from "../shared/phone-input/phone-input";
import type { AccountActivationFormInputs } from "./form-validation/account-activation.schema";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { PasswordInput } from "../shared/password-input/password-input.content";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared-kernel/primary/shared/shadcn/ui/card";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";

export type ActivatedUser = { name: string; lastName: string; email: string; mobile: Nullable<string> };

interface Props {
  user: ActivatedUser;
  form: UseFormReturn<AccountActivationFormInputs>;
  onSubmit: FormSubmit;
}

export const AccountActivation = ({ user, form: methods, onSubmit }: Props) => {
  const {
    formState: { isSubmitting },
    watch,
  } = methods;

  return (
    <Form {...methods}>
      <Card className="mx-auto mt-4 w-full max-w-[400px]">
        <CardHeader>
          <CardTitle>Activer mon compte</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit}>
            <div className="space-y-4 sm:space-y-6">
              {user.email && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              )}
              <p className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                Indiquez votre prénom et nom. Votre nom de scène vous sera demandé plus tard.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={methods.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prénom</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={methods.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom de famille</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={methods.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Téléphone</FormLabel>
                    <FormControl>
                      <Suspense fallback={<Skeleton className="h-10 w-full" />}>
                        <PhoneInput phone={field.value} onChange={value => field.onChange(value as string)} />
                      </Suspense>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <PasswordInput control={methods.control} password={watch("password")} />
              <p className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                Vous pourrez ajouter votre photo de profil une fois connecté.
              </p>
              <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                  Activer mon compte
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </Form>
  );
};
